<template>
  <BlokkliProvider v-bind="blokkliProps">
    <div class="pt-20 md:pt-60 lg:pt-90">
      <Container>
        <h1 v-if="title" class="mb-30 h1 font-bold hyphens-auto">
          {{ title }}
        </h1>

        <div class="text-primary-500 font-medium">
          <DrupalDateTime
            v-if="date?.first"
            v-bind="date.first"
            :date-only="false"
          />
        </div>

        <Tag class="mt-10" :text="labelTitleWithFallback" />

        <div v-if="organization || senderInfo" class="mt-10 mb-30">
          <p v-if="organization?.label">{{ organization.label }}</p>
          <p v-if="senderInfo">{{ senderInfo }}</p>
        </div>

        <p v-if="lead" class="mt-10 mb-20 lead xl:pr-140 hyphens-auto">
          {{ lead }}
        </p>

        <MediaImage
          v-if="image"
          v-blokkli-droppable:[mediaFieldName]
          v-bind="image"
          :image-style="imageStylePressRelease"
          class="mt-30 mb-40"
          img-class="w-full"
          loading="eager"
          preload
          rounded
        />
      </Container>

      <!-- Main content. -->
      <BlokkliField :list="paragraphs" name="field_paragraphs" />

      <Container>
        <div v-if="hint">
          <h2 class="text-2xl font-bold mb-20">
            {{ $texts('hints', 'Hinweise') }}
          </h2>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="ck-content" v-html="hint" />
        </div>

        <div class="grid md:grid-cols-2 gap-20 mt-40 mb-20">
          <!-- Presence of url has been checked in the filtered list. -->
          <a
            v-for="(attachment, i) in attachmentsFiltered"
            :key="i"
            class="link with-icon"
            target="_blank"
            :href="
              'originalFileUrl' in attachment
                ? attachment.originalFileUrl
                : attachment.file!.entity!.uri!.first!.url
            "
          >
            {{ attachment.title }}
          </a>

          <VuepalLink
            v-for="(link, key) in renderableLinks"
            :key="key"
            :to="link.uri?.url"
            :is-link="false"
            class="link with-icon"
          >
            {{ link.title }}
          </VuepalLink>
        </div>
      </Container>
    </div>

    <BoxSection v-if="organization?.label || paragraphsContact?.length">
      <BoxSectionItemParagraphs
        :list="paragraphsContact"
        :title="$texts('pressRelease.forMoreInformation', 'Weitere Auskünfte')"
      />
      <BoxSectionItemOrganization :organization="organization" />
    </BoxSection>
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type {
  AttachmentsPressReleaseFragment,
  ImagePressReleaseFragment,
  NodePressReleaseFragment,
  TaxonomyTermOrganizationUnitFragment,
} from '#graphql-operations'

const { $texts } = useNuxtApp()

type Attachment = AttachmentsPressReleaseFragment | ImagePressReleaseFragment

const props = withDefaults(
  defineProps<{
    isCallForWitnesses?: NodePressReleaseFragment['isCallForWitnesses']
    title?: string
    bundle?: string
    lead?: string
    labelTitle?: string
    senderInfo?: string
    hint?: string
    fieldOrganization?: NodePressReleaseFragment['fieldOrganization']
    attachments?: NodePressReleaseFragment['attachments']
    date?: NodePressReleaseFragment['date']
    links?: NodePressReleaseFragment['links']
    image?: NodePressReleaseFragment['image']
    paragraphs?: NodePressReleaseFragment['paragraphs']
    paragraphsContact?: NodePressReleaseFragment['paragraphsContact']
    mediaFieldName?: string
    blokkliProps: NodePressReleaseFragment['blokkliProps']
  }>(),
  {
    isCallForWitnesses: false,
    bundle: undefined,
    title: undefined,
    lead: undefined,
    labelTitle: undefined,
    senderInfo: undefined,
    hint: undefined,
    fieldOrganization: undefined,
    attachments: undefined,
    date: undefined,
    links: undefined,
    image: undefined,
    paragraphs: undefined,
    paragraphsContact: undefined,
    canEdit: false,
    mediaFieldName: 'field_media_image',
    blokkliProps: undefined,
  },
)

const labelTitleWithFallback = computed(() => {
  // If the item isCallForWitnesses then we want to show the label as
  // "Zeugenaufruf" instead of "Medienmitteilung".
  return props.labelTitle === 'News'
    ? $texts('news.tag', 'News')
    : props.labelTitle || props.isCallForWitnesses
      ? $texts('callForWitnesses.tag', 'Zeugenaufruf')
      : $texts('pressRelease.tag', 'Medienmitteilung')
})

/**
 * The organization entity.
 *
 * Field does not restrict which vocabulary can be referenced, so we have to
 * make sure it's actually an organization.
 */
const organization = computed<TaxonomyTermOrganizationUnitFragment | undefined>(
  () => {
    const v = props.fieldOrganization
    if (v && 'label' in v) {
      return v
    }
    return undefined
  },
)

const attachmentsFiltered = computed<Attachment[]>(() => {
  return (
    props.attachments?.filter(
      (v) =>
        v &&
        ('originalFileUrl' in v ||
          ('file' in v && v.file?.entity?.uri?.first?.url)),
    ) || []
  )
})

const renderableLinks = computed(() => {
  return props.links?.filter((link) => !!link.uri?.url)
})

const imageStylePressRelease = defineImageStyle({
  type: 'sizes',
  sizes: {
    sm: 400,
    md: 728,
    lg: 796,
    xl: 1040,
  },
})

useCDNHeaders((v) => v.addTags(['nuxt:page:press_release']))
</script>
